import { useMutation, useQueryClient } from '@tanstack/react-query';

import { creditsKeys } from '#creditFeature/models/credits/keys';
import { forceSyncCapabilities } from '#lib/authToken';
import { captureException } from '#lib/sentry';
import { orgKeys } from '#orgFeature/models/org/keys';

import {
	createCheckoutSession,
	createPortalSession,
	changeSubscription,
	addSeat,
	startAddon,
	cancelAddon,
	cancelSubscription,
	revertCancellation,
	pauseSubscription,
	resumeSubscription,
	recordRetentionFlowEvent,
} from '../../api/billingService';

import { billingKeys } from './keys';

export function useCreateCheckoutSession() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ priceId }) => createCheckoutSession(priceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
		},
		onError: (error, variables) => {
			// Track error in Sentry
			captureException(error, {
				tags: {
					section: 'billing',
					operation: 'create-checkout-session',
				},
				extra: {
					priceId: variables.priceId,
				},
				level: 'error',
			});
		},
	});
}

export function useCreatePortalSession() {
	return useMutation({
		mutationFn: createPortalSession,
		onError: (error) => {
			// Track error in Sentry
			captureException(error, {
				tags: {
					section: 'billing',
					operation: 'create-portal-session',
				},
				level: 'error',
			});
		},
	});
}

export function useChangeSubscription() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: changeSubscription,
		onSuccess: async () => {
			// Force refresh of capabilities (user may have fewer rights after plan change)
			await forceSyncCapabilities();

			// Invalidate subscription data
			queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });

			// Invalidate all credits queries (wallet, transactions, quotas)
			queryClient.invalidateQueries({ queryKey: creditsKeys.all });
		},
		onError: (error, variables) => {
			// Track error in Sentry
			captureException(error, {
				tags: {
					section: 'billing',
					operation: 'change-subscription',
				},
				extra: {
					newPriceId: variables,
				},
				level: 'error',
			});
		},
	});
}

export function useAddSeat() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: addSeat,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
		},
		onError: (error) => {
			captureException(error, {
				tags: {
					section: 'billing',
					operation: 'add-seat',
				},
				level: 'error',
			});
		},
	});
}

/**
 * Starts/activates an add-on (mutate with the addonKey, e.g. 'crm'). The add-on's capability
 * (`<addonKey>.access`) only appears in the JWT after a refresh, so we sync capabilities in
 * onSuccess BEFORE callers navigate — else ProtectedAppRoute reads stale caps and bounces.
 */
export function useStartAddon() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ['billing', 'addon', 'start'],
		mutationFn: (addonKey) => startAddon(addonKey),
		onSuccess: async () => {
			await forceSyncCapabilities();
			queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
		},
		onError: (error, addonKey) => {
			captureException(error, {
				tags: { section: 'billing', operation: 'start-addon' },
				extra: { addonKey },
				level: 'error',
			});
		},
	});
}

/**
 * Cancels an add-on at period end (mutate with the addonKey). Access (capability) persists until
 * rollover; we still sync caps + refetch org info so the UI reflects the pending-cancel state.
 */
export function useCancelAddon() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ['billing', 'addon', 'cancel'],
		mutationFn: (addonKey) => cancelAddon(addonKey),
		onSuccess: async () => {
			await forceSyncCapabilities();
			queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
		},
		onError: (error, addonKey) => {
			captureException(error, {
				tags: { section: 'billing', operation: 'cancel-addon' },
				extra: { addonKey },
				level: 'error',
			});
		},
	});
}

/**
 * Shared shape of the subscription lifecycle mutations (cancel / revert / pause / resume). Each returns
 * the fresh subscription payload, which is written into the cache before the invalidations so the
 * billing page never flashes the pre-mutation state; capabilities are re-synced because a pause changes
 * the JWT's plan capabilities.
 * @param {string} operation - Sentry tag + mutation key segment, e.g. 'cancel-subscription'
 * @param {Function} mutationFn
 * @returns {Object} mutation result
 */
function useSubscriptionLifecycleMutation(operation, mutationFn) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ['billing', 'subscription', operation],
		mutationFn,
		onSuccess: async ({ data }) => {
			queryClient.setQueryData(billingKeys.subscription(), data.subscription);
			try {
				await forceSyncCapabilities();
			} catch (error) {
				// Stripe has already applied the change; a failed JWT refresh must not surface as a mutation
				// error (the next fetcher call refreshes the token anyway).
				captureException(error, { tags: { section: 'billing', operation }, level: 'warning' });
			}
			queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
			queryClient.invalidateQueries({ queryKey: creditsKeys.all });
		},
		onError: (error, variables) => {
			// The free-text comment is customer data and must never reach Sentry.
			captureException(error, {
				tags: { section: 'billing', operation },
				extra: { reason: variables?.reason, months: variables?.months, offerShown: variables?.offerShown },
				level: 'error',
			});
		},
	});
}

export function useCancelSubscription() {
	return useSubscriptionLifecycleMutation('cancel-subscription', cancelSubscription);
}

export function useRevertCancellation() {
	return useSubscriptionLifecycleMutation('revert-cancellation', revertCancellation);
}

export function usePauseSubscription() {
	return useSubscriptionLifecycleMutation('pause-subscription', pauseSubscription);
}

export function useResumeSubscription() {
	return useSubscriptionLifecycleMutation('resume-subscription', resumeSubscription);
}

// Telemetry only: a failed flow event must never surface to the customer or block the flow.
export function useRecordRetentionFlowEvent() {
	return useMutation({
		mutationFn: recordRetentionFlowEvent,
		meta: { suppressGlobalError: true },
	});
}

export function usePlanSelect(currentSubscription) {
	const queryClient = useQueryClient();
	const createCheckoutMutation = useCreateCheckoutSession();
	const changeSubscriptionMutation = useChangeSubscription();

	return useMutation({
		mutationFn: async (priceId) => {
			if (currentSubscription) {
				// User has existing subscription - change instead of creating new
				return await changeSubscriptionMutation.mutateAsync(priceId);
			} else {
				// New user - create checkout session
				const { data } = await createCheckoutMutation.mutateAsync({ priceId });

				if (data?.url) {
					window.location.href = data.url;
				}
				return data;
			}
		},
		onSuccess: () => {
			// If this was a subscription change (not a checkout redirect), invalidate credits
			if (currentSubscription) {
				queryClient.invalidateQueries({ queryKey: creditsKeys.all });
			}
		},
		onError: (error, priceId) => {
			// Track error in Sentry
			captureException(error, {
				tags: {
					section: 'billing',
					operation: 'plan-select',
				},
				extra: {
					priceId,
					hasExistingSubscription: Boolean(currentSubscription),
					subscriptionId: currentSubscription?.id,
				},
				level: 'error',
			});
		},
	});
}
